<template>
    <SignaloDialog
        :icon="'mdi-cog-outline'"
        :title="item?.name"
        :open="open"
        @close="closeDialog"
    >
        <template #body>
            <SignaloPageLoading v-if="loading" />
            <PageNotFound v-else-if="!item" />
            <div v-else>
                <v-tabs v-model="tabs" color="primary">
                    <v-tab :value="data"> {{ $t('common.data') }} </v-tab>
                    <v-tab :value="desc">
                        {{ $t('common.description') }}
                    </v-tab>
                    <v-tab
                        :value="documents"
                        v-if="editedItem && editedItem.id"
                    >
                        {{ $t('common.documents') }}
                    </v-tab>
                    <v-tab :value="photos" v-if="editedItem && editedItem.id">
                        {{ $t('common.images') }}
                    </v-tab>
                </v-tabs>
                <v-window v-model="tabs" class="ma-0">
                    <v-window-item key="data">
                        <v-card-text>
                            <v-form
                                ref="dataForm"
                                v-model="dataValid"
                                lazy-validation
                                class="v-data-form"
                            >
                                <v-row>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-text-field
                                            :label="$t('common.name')"
                                            v-model="item.name"
                                            :max="4"
                                            :rules="nameRules"
                                            required
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            class="required-field"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-select
                                            :label="$t('common.unitType')"
                                            v-model="unitType"
                                            @update:modelValue="
                                                onUnitTypeChange
                                            "
                                            :items="unitTypes"
                                            :item-title="`translations[${$i18n.locale}]`"
                                            item-value="value"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            class="required-field"
                                        >
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-select
                                            :label="$t('common.unit')"
                                            v-model="item.preferred_unit"
                                            :items="units"
                                            item-value="value"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            class="required-field padding-0-select"
                                            :rules="unitRules"
                                            ref="unitSelectRef"
                                            :disabled="!unitType"
                                        >
                                            <template #selection="{ item }">
                                                <v-list-item
                                                    :value="item.value"
                                                >
                                                    <span
                                                        v-html="
                                                            $displayDimension(
                                                                item.value
                                                            )
                                                        "
                                                    ></span>
                                                </v-list-item>
                                            </template>
                                            <template #item="{ item }">
                                                <v-list-item
                                                    @click="setUnit(item)"
                                                    style="cursor: pointer"
                                                >
                                                    <span
                                                        v-html="
                                                            $displayDimension(
                                                                item.value
                                                            )
                                                        "
                                                    ></span>
                                                </v-list-item>
                                            </template>
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <SignaloDictionaryTreePicker
                                            :group="'part_category'"
                                            v-model="item.category"
                                            v-slot="props"
                                            ref="dictionaryTreePicker"
                                            v-if="
                                                dataStore.dictionaries.items.find(
                                                    (d) =>
                                                        d.name ===
                                                        'part_category'
                                                )?.is_tree
                                            "
                                        >
                                            <v-text-field
                                                :modelValue="props.text"
                                                @click:control="props.onClick"
                                                @click:clear="props.onClear"
                                                clearable
                                                readonly
                                                hide-details
                                                :label="$t('common.category')"
                                                clear-icon="mdi-close-circle-outline"
                                                variant="outlined"
                                                color="var(--greyscale-100)"
                                            />
                                        </SignaloDictionaryTreePicker>
                                        <v-select
                                            v-else
                                            :label="$t('common.category')"
                                            :modelValue="
                                                part_category.find(
                                                    (x) =>
                                                        x.keyname ==
                                                        item.category
                                                )
                                            "
                                            @update:modelValue="
                                                (x) => (item.category = x)
                                            "
                                            :items="part_category"
                                            item-title="value"
                                            item-value="keyname"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                        >
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-text-field
                                            :label="$t('common.symbol')"
                                            v-model="item.symbol"
                                            :counter="100"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-text-field
                                            :label="
                                                $t(
                                                    'tableHeaders.registryNumber'
                                                )
                                            "
                                            v-model="item.registry_no"
                                            :counter="100"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-text-field
                                            :label="
                                                $t('common.minimumQuantity')
                                            "
                                            v-model="item.min_quantity"
                                            :rules="minQuantityRules"
                                            type="number"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            min="0"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-text-field
                                            :label="
                                                $t('common.optimalQuantity')
                                            "
                                            v-model="item.opt_quantity"
                                            :rules="optQuantityRules"
                                            type="number"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-select
                                            :label="
                                                $t('common.abcClassification')
                                            "
                                            :modelValue="
                                                part_priority.find(
                                                    (x) =>
                                                        x.keyname ==
                                                        item.priority
                                                )
                                            "
                                            @update:modelValue="
                                                (x) => (item.priority = x)
                                            "
                                            :items="part_priority"
                                            item-title="value"
                                            item-value="keyname"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            :rules="[$REQUIRED_RULE]"
                                            class="required-field"
                                        >
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-select
                                            :label="
                                                $t('common.xyzClassification')
                                            "
                                            :modelValue="
                                                part_demand.find(
                                                    (x) =>
                                                        x.keyname == item.demand
                                                )
                                            "
                                            @update:modelValue="
                                                (x) => (item.demand = x)
                                            "
                                            :items="part_demand"
                                            item-title="value"
                                            item-value="keyname"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            :rules="[$REQUIRED_RULE]"
                                            class="required-field"
                                        >
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-select
                                            :label="$t('common.manufacturer')"
                                            :modelValue="
                                                dataStore.companies.getById(
                                                    item.manufacturer_id
                                                )
                                            "
                                            @update:modelValue="
                                                (x) =>
                                                    (item.manufacturer_id = x)
                                            "
                                            :items="companies"
                                            item-title="name"
                                            item-value="id"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            clearable
                                        >
                                        </v-select>
                                    </v-col>
                                    <v-col cols="12" md="4" xl="3">
                                        <v-select
                                            :label="$t('common.mainSupplier')"
                                            :modelValue="
                                                dataStore.companies.getById(
                                                    item.main_supplier_id
                                                )
                                            "
                                            @update:modelValue="
                                                (x) =>
                                                    (item.main_supplier_id = x)
                                            "
                                            :items="companies"
                                            item-title="name"
                                            item-value="id"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                            clearable
                                        >
                                        </v-select>
                                    </v-col>
                                </v-row>
                            </v-form>
                        </v-card-text>
                    </v-window-item>
                    <v-window-item key="desc">
                        <v-card-text>
                            <v-form
                                ref="dataForm"
                                v-model="dataValid"
                                lazy-validation
                                class="v-data-form"
                            >
                                <v-row>
                                    <v-col cols="12" md="12" xl="12">
                                        <v-textarea
                                            :label="$t('common.description')"
                                            v-model="item.description"
                                            variant="outlined"
                                            color="var(--greyscale-100)"
                                        >
                                        </v-textarea>
                                    </v-col>
                                </v-row>
                            </v-form>
                        </v-card-text>
                    </v-window-item>
                    <v-window-item key="documents" :value="documents">
                        <v-card-text>
                            <SignaloFilesDocumentsContainer
                                :endpoint="`/parts/${item.id}/documents`"
                                allow-upload
                                allow-remove
                            />
                        </v-card-text>
                    </v-window-item>
                    <v-window-item key="photos" :value="photos">
                        <v-card-text>
                            <SignaloFilesImagesContainer
                                :endpoint="`/parts/${item.id}/photos`"
                                allow-upload
                                allow-remove
                            />
                        </v-card-text>
                    </v-window-item>
                </v-window>
            </div>
        </template>
        <template #buttons>
            <v-btn @click="closeDialog">
                {{ $t('common.cancel') }}
            </v-btn>
            <v-btn
                :loading="dataSaving"
                color="primary"
                variant="elevated"
                @click="saveData"
            >
                {{ $t('common.save') }}
            </v-btn>
        </template>
    </SignaloDialog>
</template>

<script>
import * as AUTH_STORE from 'signalo-vue-auth-store';
import * as DATA_STORE from 'signalo-vue-data-store';
import * as SIGNALO_VUE_HELPERS_STORE from 'signalo-vue-helpers-store';

export default {
    setup() {
        const authStore = AUTH_STORE.default();
        const dataStore = DATA_STORE.default();
        const helpersStore = SIGNALO_VUE_HELPERS_STORE.default();
        return { dataStore, helpersStore, authStore };
    },
    props: ['open', 'editedItem'],
    data() {
        return {
            id: this.editedItem?.id,

            item: null,
            loading: true,

            tabs: 'data',

            // DATA
            dataValid: true,
            dataSaving: false,
            dimensions: [],
            unitTypes: [],
            units: [],
            unitType: null,
        };
    },
    computed: {
        part_category() {
            return Object.values(
                this.dataStore.dictionary.group('part_category')
            );
        },
        part_priority() {
            return Object.values(
                this.dataStore.dictionary.group('part_priority')
            );
        },
        part_demand() {
            return Object.values(
                this.dataStore.dictionary.group('part_demand')
            );
        },
        locations() {
            return this.dataStore.locations.values;
        },
        companies() {
            return this.dataStore.companies.values;
        },
        nameRules() {
            return [(v) => !!v || this.$t('common.nameRequired')];
        },
        yearRules() {
            return [(v) => /^\d{4}$/.test(v) || this.$t('common.formatYYYY')];
        },
        unitRules() {
            return [(v) => !!v || this.$t('common.fieldRequired')];
        },
        minQuantityRules() {
            return [
                (v) =>
                    !v ||
                    v >= 0 ||
                    this.$t('common.quantityGreaterThan') + ` 0`,
            ];
        },
        optQuantityRules() {
            return [
                (v) =>
                    !v ||
                    !this.item.min_quantity ||
                    (v && Number(v) >= Number(this.item.min_quantity)) ||
                    this.$t('common.quantityGreaterThan') +
                        ` ${this.item.min_quantity - 1}`,
            ];
        },
    },
    mounted() {
        this.load();
    },
    methods: {
        load() {
            this.loading = true;

            this.dataStore.dictionaries.promise();
            const loadRequests = [
                this.dataStore.dictionary.promise(),
                this.dataStore.locations.promise(),
                this.dataStore.companies.promise(),
                this.axios.get('global/dimensions'),
            ];

            if (this.id) {
                loadRequests.unshift(this.axios.get('/parts/' + this.id));
            }

            Promise.allSettled(loadRequests).then((promises) => {
                this.item = {};
                if (this.id && promises[0].status === 'fulfilled') {
                    let x = promises[0].value;
                    if (x.data.status == 'ok') {
                        this.item = x.data.item;
                    }
                }

                this.unitTypes = Object.keys(
                    promises[promises.length - 1]?.value?.data
                )
                    ?.slice(0, -1)
                    ?.map((key) => {
                        return {
                            value: key,
                            translations:
                                promises[promises.length - 1]?.value?.data[key]
                                    ?.translations,
                            units: promises[promises.length - 1]?.value?.data[
                                key
                            ]?.units,
                        };
                    });
                if (this.id) {
                    this.unitType = this.unitTypes.find((ut) =>
                        ut.units.includes(this.item.preferred_unit)
                    )?.value;
                }

                this.setDefaults();

                this.loading = false;
            });
        },
        setDefaults() {
            if (this.editedItem?.id) return;
            if (this.authStore.tenant.attributes['part_default_priority']) {
                this.item.priority =
                    this.authStore.tenant.attributes['part_default_priority'];
            }
            if (this.authStore.tenant.attributes['part_default_demand']) {
                this.item.demand =
                    this.authStore.tenant.attributes['part_default_demand'];
            }
        },
        async saveData() {
            await this.$refs.dataForm.validate();
            if (!this.dataValid) return;
            this.dataSaving = true;
            let request;

            if (this.id) {
                request = this.axios.request({
                    url: `/parts/${this.id}`,
                    method: 'PUT',
                    data: this.item,
                });
            } else {
                request = this.axios.request({
                    url: `/parts`,
                    method: 'POST',
                    data: this.item,
                });
            }

            request
                .then((res) => {
                    this.helpersStore.snackbar(
                        this.$t('common.saved'),
                        'success'
                    );
                    this.closeDialog(true, res?.data?.item);
                })
                .catch((e) => {
                    if (e?.response?.status == 401) return;
                })
                .finally(() => {
                    this.dataSaving = false;
                });
        },
        closeDialog(reloadData = false, item = null) {
            this.$emit('closeDialog', reloadData, item);
        },
        onUnitTypeChange(val) {
            this.item.preferred_unit = null;
            this.units = this.unitTypes.find((ut) => ut.value === val)?.units;
        },
        setUnit(val) {
            this.item.preferred_unit = val.value;
            this.$refs.unitSelectRef.menu = false;
        },
    },
};
</script>

<style>
.eq-photo {
    position: relative;
    margin: 10px;
    width: 150px;
    height: 150px;
    border: 1px solid gray;
    border-radius: 10px;
    cursor: pointer;
    z-index: 101;
    overflow: hidden;
}

.eq-photo .cover {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 100;
}

.eq-photo .cover:hover {
    background: rgba(200, 200, 200, 0.6);
}

.window-container {
    height: 500px;
    width: 1200px;
}

.v-data-form {
    display: flex;
    flex-direction: column;
    height: 100%;
}
</style>
